import { useState } from 'react'
import {
  X, Trash2, RotateCcw, AlertTriangle, Loader2, FileIcon, Folder, Search
} from 'lucide-react'
import { useTrash } from '../hooks/useTrash'

interface TrashModalProps {
  isOpen: boolean
  onClose: () => void
  onChange?: () => void  // fired after restore/purge so parent can reload listing
}

export function TrashModal({ isOpen, onClose, onChange }: TrashModalProps) {
  const {
    items, loading, busyId, confirmEmpty, setConfirmEmpty,
    handleRestore, handlePurge, handleEmpty, isAdmin,
  } = useTrash(isOpen, onChange)
  const [filter, setFilter] = useState('')

  if (!isOpen) return null

  const filtered = items.filter(i =>
    !filter.trim() ||
    i.name.toLowerCase().includes(filter.toLowerCase()) ||
    i.path.toLowerCase().includes(filter.toLowerCase())
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-3xl bg-dark-800 border border-dark-600 rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between border-b border-dark-600 p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-red-500 to-red-600">
              <Trash2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Trash</h2>
              <p className="text-xs text-dark-400">Items are auto-deleted after 30 days</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-dark-400 hover:bg-dark-700 hover:text-white transition"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex items-center gap-2 border-b border-dark-700 p-4">
          <div className="flex items-center gap-2 flex-1">
            <Search className="h-4 w-4 text-dark-400" />
            <input
              type="text"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Filter by name or path…"
              className="flex-1 rounded-lg border border-dark-600 bg-dark-900/50 px-3 py-2 text-sm text-white placeholder-dark-500 focus:border-primary-500 focus:outline-none"
            />
          </div>
          {isAdmin && items.length > 0 && (
            confirmEmpty ? (
              <div className="flex gap-2">
                <button
                  onClick={handleEmpty}
                  disabled={busyId === '__all__'}
                  className="rounded-lg border border-red-500/40 bg-red-500/20 px-3 py-2 text-xs font-medium text-red-300 hover:bg-red-500/30 disabled:opacity-50"
                >
                  {busyId === '__all__' ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Confirm empty'}
                </button>
                <button
                  onClick={() => setConfirmEmpty(false)}
                  className="rounded-lg border border-dark-600 bg-dark-900/60 px-3 py-2 text-xs text-dark-400 hover:text-white"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmEmpty(true)}
                className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs font-medium text-red-400 transition hover:bg-red-500/20"
              >
                Empty trash
              </button>
            )
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-dark-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-sm text-dark-400">
              {items.length === 0 ? 'Trash is empty.' : 'No items match your filter.'}
            </div>
          ) : (
            <ul className="divide-y divide-dark-700">
              {filtered.map(item => {
                const isBusy = busyId === item.id
                return (
                  <li key={`${item.type}-${item.id}`} className="flex items-center gap-3 p-4 hover:bg-dark-900/40 transition">
                    {item.type === 'folder'
                      ? <Folder className="h-5 w-5 flex-shrink-0 text-blue-400" />
                      : <FileIcon className="h-5 w-5 flex-shrink-0 text-dark-400" />}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-white">{item.name}</p>
                      <p className="truncate text-xs text-dark-400 font-mono">{item.path}</p>
                    </div>
                    <div className="hidden sm:block text-right text-xs text-dark-400 whitespace-nowrap">
                      Trashed<br />
                      {new Date(item.deletedAt).toLocaleDateString()}
                    </div>
                    <button
                      onClick={() => handleRestore(item)}
                      disabled={isBusy}
                      className="flex items-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-300 transition hover:bg-emerald-500/20 disabled:opacity-50"
                      title="Restore"
                    >
                      {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                      Restore
                    </button>
                    <button
                      onClick={() => handlePurge(item)}
                      disabled={isBusy}
                      className="rounded-lg border border-red-500/30 bg-red-500/10 p-2 text-red-400 transition hover:bg-red-500/20 disabled:opacity-50"
                      title="Delete permanently"
                    >
                      {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className="flex items-start gap-2 border-t border-dark-700 bg-dark-900/60 p-4 text-xs text-dark-400">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-yellow-400" />
          <span>Restoring an item puts it back at its original path. If a new item now lives there, restore is refused — rename the new one first, then retry.</span>
        </div>
      </div>
    </div>
  )
}
